import { fetchAuthSession } from '@aws-amplify/core';

import api from '../axios';

export type RedZoneObservationEntityType =
  'VEHICLE' | 'PERSON' | 'ACTIVITY' | 'HAZARD' | 'OTHER';

export type RedZoneObservationClassification =
  'BASELINE' | 'UNUSUAL' | 'SUSPICIOUS' | 'THREAT';

export type RedZoneObservationLocationSource =
  'MAP_DROP' | 'ADDRESS_SEARCH' | 'MOBILE_GPS' | 'MANUAL';

export type RedZoneTravelDirection =
  'NORTH' | 'SOUTH' | 'EAST' | 'WEST' | 'STATIONARY';

export type RedZoneRiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type RedZoneReportStatus =
  'DRAFT' | 'SUBMITTED' | 'PENDING_REVIEW' | 'ESCALATED';

export type RedZoneIncidentType =
  | 'SUSPICIOUS_VEHICLE'
  | 'SUSPICIOUS_PERSON'
  | 'SUSPICIOUS_ACTIVITY'
  | 'SURVEILLANCE_RECON'
  | 'APPROACH_BEHAVIOR'
  | 'LOITERING'
  | 'PHOTOGRAPHY_VIDEO'
  | 'FOLLOWING'
  | 'ACCESS_ATTEMPT'
  | 'OTHER';

export type RedZoneAttachmentType =
  'VEHICLE_PHOTO' | 'PERSON_PHOTO' | 'GENERAL';

export type RedZoneAttachment = {
  _id?: string;
  type: RedZoneAttachmentType;
  storageKey: string;
  fileName?: string;
  mimeType?: string;
  size?: number;

  /**
   * Populated only by detail endpoints once we add
   * temporary signed S3 URLs for inspection.
   */
  url?: string;
};

export type RedZoneVehicle = {
  licensePlate?: string;
  state?: string;
  make?: string;
  model?: string;
  color?: string;
  bodyType?: string;
  direction?: RedZoneTravelDirection;
  occupants?: string;
  distinguishingMarks?: string;
};

export type RedZonePerson = {
  gender?: string;
  approximateAge?: string;
  height?: string;
  build?: string;
  clothing?: string;
  hair?: string;
  identifyingFeatures?: string;
  behavior?: string;
  associatedVehicle?: string;
};

export type RedZoneObservationLocation = {
  type: 'Point';
  coordinates: [number, number]; // GeoJSON: [longitude, latitude]
  formattedAddress?: string;
  placeId?: string;
  source: RedZoneObservationLocationSource;
};

export type RedZoneObservation = {
  _id: string;
  associatedCompany: string;
  associatedSite: string;
  observedAt: string;
  protectorId?: string;
  protectorNameSnapshot?: string;
  createdByUserId?: string;
  location: RedZoneObservationLocation;
  entityType: RedZoneObservationEntityType;
  classification: RedZoneObservationClassification;
  direction?: RedZoneTravelDirection;
  incidentType?: RedZoneIncidentType;
  riskLevel?: RedZoneRiskLevel;
  narrative?: string;
  vehicle?: RedZoneVehicle;
  person?: RedZonePerson;

  /**
   * Observation evidence stored in S3.
   *
   * GENERAL is used for map-observation photos.
   */
  attachments?: RedZoneAttachment[];

  reportId?: string;
  escalatedAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateRedZoneObservationPayload = {
  siteId: string;
  observedAt?: string;
  protectorId?: string;
  protectorNameSnapshot?: string;
  location: {
    coordinates: [number, number];
    formattedAddress?: string;
    placeId?: string;
    source?: RedZoneObservationLocationSource;
  };
  entityType: RedZoneObservationEntityType;
  classification?: RedZoneObservationClassification;
  direction?: RedZoneTravelDirection | '';
  incidentType?: RedZoneIncidentType | '';
  riskLevel?: RedZoneRiskLevel | '';
  narrative?: string;
  vehicle?: RedZoneVehicle;
  person?: RedZonePerson;
};

export type UpdateRedZoneObservationPayload = Partial<
  Omit<CreateRedZoneObservationPayload, 'siteId'>
> & {
  protectorId?: string | null;
  protectorNameSnapshot?: string | null;
  direction?: RedZoneTravelDirection | '' | null;
  incidentType?: RedZoneIncidentType | '' | null;
  riskLevel?: RedZoneRiskLevel | '' | null;
  narrative?: string | null;
  vehicle?: RedZoneVehicle | null;
  person?: RedZonePerson | null;
};

export type GetRedZoneObservationsFilters = {
  siteId: string;
  from?: string;
  to?: string;
  entityType?: RedZoneObservationEntityType;
  classification?: RedZoneObservationClassification;
  protectorId?: string;
};

export type CreateRedZoneReportPayload = {
  siteId: string;
  observationId?: string;
  locationId?: string;
  observedAt?: string;
  protectorId?: string;
  direction?: RedZoneTravelDirection | '';
  incidentType?: RedZoneIncidentType | '';
  riskLevel?: RedZoneRiskLevel | '';
  status?: RedZoneReportStatus;
  narrative?: string;
  entities?: {
    vehicle: boolean;
    person: boolean;
  };
  vehicle?: RedZoneVehicle;
  person?: RedZonePerson;
};

export type ApiResponse<T = any> = {
  success: boolean;
  data?: T;
  msg?: string;
};

async function getIdToken(): Promise<string> {
  const { tokens } = await fetchAuthSession();
  const idToken = tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error('NO SESSION');
  }

  return idToken;
}

function authHeaders(idToken: string) {
  return {
    Authorization: `Bearer ${idToken}`,
  };
}

export async function createRedZoneObservation(
  payload: CreateRedZoneObservationPayload,
  photos: File[] = []
): Promise<ApiResponse<RedZoneObservation>> {
  const idToken = await getIdToken();

  if (photos.length > 5) {
    throw new Error(
      'A maximum of 5 photos may be attached to a Red Zone observation.'
    );
  }

  /*
   * Keep the existing lightweight JSON path for observations
   * without photos.
   *
   * BASELINE and UNUSUAL may use this path.
   */
  if (photos.length === 0) {
    const res = await api.post(
      '/red-zone/observations',
      {
        ...payload,
        token: idToken,
      },
      {
        headers: authHeaders(idToken),
      }
    );

    const raw = res.data;

    return {
      success: Boolean(raw?.success),
      msg: raw?.msg,
      data: raw?.data?.observation,
    };
  }

  /*
   * When evidence is attached, send the observation as multipart:
   *
   * payload             -> JSON string
   * observationPhotos   -> repeated File fields, max 5
   *
   * Do NOT manually set Content-Type. The browser/Axios must add
   * the multipart boundary.
   */
  const formData = new FormData();

  formData.append('payload', JSON.stringify(payload));

  for (const photo of photos) {
    formData.append('observationPhotos', photo);
  }

  const res = await api.post('/red-zone/observations', formData, {
    headers: authHeaders(idToken),
  });

  const raw = res.data;

  return {
    success: Boolean(raw?.success),
    msg: raw?.msg,
    data: raw?.data?.observation,
  };
}

export async function getRedZoneObservations(
  filters: GetRedZoneObservationsFilters
): Promise<ApiResponse<RedZoneObservation[]>> {
  const idToken = await getIdToken();
  const params: Record<string, string> = {
    siteId: filters.siteId,
  };

  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;
  if (filters.entityType) params.entityType = filters.entityType;
  if (filters.classification) params.classification = filters.classification;
  if (filters.protectorId) params.protectorId = filters.protectorId;

  const res = await api.get('/red-zone/observations', {
    params,
    headers: authHeaders(idToken),
  });

  const raw = res.data;

  return {
    success: Boolean(raw?.success),
    msg: raw?.msg,
    data: Array.isArray(raw?.data?.observations) ? raw.data.observations : [],
  };
}

export async function getRedZoneObservationById(
  observationId: string
): Promise<ApiResponse<RedZoneObservation>> {
  const idToken = await getIdToken();

  const res = await api.get(
    `/red-zone/observations/${encodeURIComponent(observationId)}`,
    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),
    msg: raw?.msg,
    data: raw?.data?.observation,
  };
}

export async function updateRedZoneObservation(
  observationId: string,
  payload: UpdateRedZoneObservationPayload
): Promise<ApiResponse<RedZoneObservation>> {
  const idToken = await getIdToken();

  const res = await api.patch(
    `/red-zone/observations/${encodeURIComponent(observationId)}`,
    {
      ...payload,
      token: idToken,
    },
    {
      headers: authHeaders(idToken),
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),
    msg: raw?.msg,
    data: raw?.data?.observation,
  };
}

export async function deleteRedZoneObservation(
  observationId: string
): Promise<ApiResponse> {
  const idToken = await getIdToken();

  const res = await api.delete(
    `/red-zone/observations/${encodeURIComponent(observationId)}`,
    {
      headers: authHeaders(idToken),
      data: {
        token: idToken,
      },
    }
  );

  const raw = res.data;

  return {
    success: Boolean(raw?.success),
    msg: raw?.msg,
    data: raw?.data,
  };
}

export async function createRedZoneReport(
  payload: CreateRedZoneReportPayload,
  attachments?: {
    vehiclePhoto?: File | null;
    personPhoto?: File | null;
  }
): Promise<ApiResponse> {
  const idToken = await getIdToken();
  const vehiclePhoto = attachments?.vehiclePhoto ?? null;
  const personPhoto = attachments?.personPhoto ?? null;

  if (vehiclePhoto || personPhoto) {
    const formData = new FormData();

    formData.append('token', idToken);
    formData.append('payload', JSON.stringify(payload));

    if (vehiclePhoto) {
      formData.append('vehiclePhoto', vehiclePhoto);
    }

    if (personPhoto) {
      formData.append('personPhoto', personPhoto);
    }

    const res = await api.post('/red-zone/create-report', formData, {
      headers: authHeaders(idToken),
    });

    return res.data;
  }

  const res = await api.post(
    '/red-zone/create-report',
    {
      ...payload,
      token: idToken,
    },
    {
      headers: authHeaders(idToken),
    }
  );

  return res.data;
}
